/* eslint-disable @altermaind/you-shall-not-pass */
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Form } from '@/_shared/components/Form';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard, WizardNavigateCallback } from '@/_shared/hooks/useWizard';
import { FileTypes } from '@/_shared/types/fileTypes';
import { getAspectCall, putAspectSaveCall, CallResponseGetAspect, useAspect } from '@/aspect/state';
import { WIZARD_STEPS } from '@/configuration/constants/wizard';
import { LoaderFullPage } from '../LoaderFullPage';

export const SetLogo = () => {
  const t = useTranslations('configuration.hr.setLogo');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    buttonGenerateLogoRef,
    inputSiteRef,
    logoLight,
    setLogoLight,
    setLogoDark,
    logoDark,
    gapXS,
    gapXXL,
    gapXXXS,
    generateLogo,
    control,
    isValid,
    handleSubmit,
    fieldValues,
    dynamicHeight,
    loadingPostAspectGenerate,
    errorPostAspectGenerate,
    errorPostAspectGenerateLogo,
    isSiteValueChanged,
    trigger,
  } = useAspect();

  const { execute: executePutAspectSave, loading: loadingPutAspectSave } = putAspectSaveCall.hook();

  const {
    execute: executeGetAspect,
    remainingDesignQuota,
    totalDesignQuota,
    success: successGetAspect,
  } = getAspectCall.hook();

  const {
    setCustomOnNext,
    setCustomOnSkip,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();

  const someInputFilled = useMemo(() => {
    return Object.keys(fieldValues)
      .filter(key => key !== 'site')
      .some((key: string) => !!fieldValues[key]);
  }, [fieldValues]);

  const onSubmit = useCallback(
    (navigate?: WizardNavigateCallback) => {
      executePutAspectSave({
        logo: {
          light: fieldValues?.uploadLogoLight?.value || '',
          dark: fieldValues?.uploadLogoDark?.value || '',
        },
        successSaga: () => {
          navigate?.();
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePutAspectSave, fieldValues.uploadLogoLight, fieldValues.uploadLogoDark, tShared],
  );

  const onSkip: NonNullable<Parameters<typeof setCustomOnSkip>[0]> = useCallback(navigate => {
    navigate?.(WIZARD_STEPS.SET_COLOR_PALETTE);
  }, []);

  useEffect(() => {
    // Fetch existing aspect data to pre-fill the form if available, for the back button functionality
    executeGetAspect({
      successSaga: ({ response }) => {
        const responseGetAspect = response as CallResponseGetAspect;
        if (responseGetAspect?.logo?.light) {
          setLogoLight(responseGetAspect.logo.light);
        }
        if (responseGetAspect?.logo?.dark) {
          setLogoDark(responseGetAspect.logo.dark);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
    setCustomOnSkip(onSkip);

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [
    handleSubmit,
    onSkip,
    onSubmit,
    resetWizardNavigationCallbacks,
    setCustomOnNext,
    setCustomOnSkip,
  ]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: !someInputFilled || loadingPutAspectSave,
      isLoadingNextButton: loadingPutAspectSave,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [
    isValid,
    loadingPutAspectSave,
    logoDark,
    logoLight,
    resetWizardNavigationButtonStatus,
    setNavigationButtonStatus,
    someInputFilled,
  ]);

  return (
    <>
      <Box mb='s'>
        <Box
          display='flex'
          flex={10}
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          style={{ gap: gapXXL }}
        >
          <Box
            width='100%'
            display='flex'
            flexDirection='column'
            justifyContent='left'
            alignItems='left'
            textAlign='left'
            flex={genericResponsiveValue({
              breakPoints: { desktop: 6 },
              defaultValue: 10,
            })}
          >
            <Box mb='m'>
              <Typography
                data-testid='set-logo-title'
                variant='h1'
                color='primary/text'
                fontSize={genericResponsiveValue({
                  breakPoints: { desktop: 50 as never },
                  defaultValue: 'xxxl',
                })}
                lineHeight={genericResponsiveValue({
                  breakPoints: { desktop: '60px' as never },
                  defaultValue: 'xxxl',
                })}
                fontWeight='bold'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'm' },
                  defaultValue: 'xs',
                })}
              >
                {t('title')}
              </Typography>
              <Typography variant='h4' fontWeight={200} mt='s' color='primary/text'>
                {t('subtitle')}
              </Typography>
            </Box>
            <Box
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              display='flex'
              style={{ gap: gapXS }}
            >
              <Box
                display={genericResponsiveValue({
                  breakPoints: { desktop: 'grid' },
                  defaultValue: 'flex',
                })}
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'row' },
                  defaultValue: 'column',
                })}
                justifyContent='space-between'
                style={{ gap: gapXS }}
                gridTemplateColumns={`repeat(${genericResponsiveValue({
                  breakPoints: { desktop: 2 },
                  defaultValue: 1,
                })}, 1fr)`}
                width='100%'
              >
                <Box
                  width={genericResponsiveValue({
                    breakPoints: { desktop: '85%', tablet: '85%' },
                    defaultValue: '100%',
                  })}
                  gridColumn='1/span 2'
                  flex={1}
                >
                  <Box display='flex' position='relative'>
                    <Box flex='1'>
                      <Form.FormInput
                        control={control}
                        additonalProps={{
                          required: true,
                          pattern: {
                            /* eslint-disable no-useless-escape */
                            value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/,
                          },
                        }}
                        avoidRequiredStarOnLabel
                        name='site'
                        data-testid='input-site'
                        label={t('siteLabel')}
                        placeholder={t('sitePlaceholder')}
                        autoComplete='off'
                        loading={loadingPostAspectGenerate}
                        flex={1}
                        inputTextStyle={{
                          pr: buttonGenerateLogoRef.current
                            ? buttonGenerateLogoRef.current.clientWidth + (gapXS || 0)
                            : undefined,
                        }}
                        inputRef={inputSiteRef}
                        disabled={remainingDesignQuota === 0}
                      />
                    </Box>
                    <Button
                      forwardedRef={buttonGenerateLogoRef}
                      variant='primary'
                      minWidth='none'
                      onClick={() => generateLogo(remainingDesignQuota)}
                      disabled={!isValid || remainingDesignQuota === 0 || loadingPostAspectGenerate}
                      px='xs'
                      position='absolute'
                      right={gapXXXS}
                      bottom={gapXXXS}
                      height={dynamicHeight}
                      data-testid='button-generate-logo'
                    >
                      {t('cta.generate')}
                    </Button>
                  </Box>
                  <Typography variant='p2/light' mt='xxs' color='grey/light'>
                    {t('siteDescription', {
                      remainingDesignQuota,
                      totalDesignQuota,
                    })}
                  </Typography>
                </Box>
                {successGetAspect && remainingDesignQuota === 0 && (
                  <Box
                    width={genericResponsiveValue({
                      breakPoints: { desktop: '85%', tablet: '85%' },
                      defaultValue: '100%',
                    })}
                    gridColumn='1/span 2'
                    flex={1}
                    mt='s'
                    mb='xxs'
                  >
                    <AlertBadge
                      iconName='info'
                      type='error'
                      label={t('remainingDesignQuotaZeroAlert')}
                    />
                  </Box>
                )}
                {!isSiteValueChanged &&
                  (errorPostAspectGenerate || errorPostAspectGenerateLogo) && (
                    <Box
                      width={genericResponsiveValue({
                        breakPoints: { desktop: '85%', tablet: '85%' },
                        defaultValue: '100%',
                      })}
                      gridColumn='1/span 2'
                      flex={1}
                      mt='s'
                      mb='xxs'
                    >
                      <AlertBadge iconName='info' type='error' label={t('alertError')} />
                    </Box>
                  )}
                <Box
                  width={genericResponsiveValue({
                    breakPoints: { desktop: '85%', tablet: '85%' },
                    defaultValue: '100%',
                  })}
                  gridColumn='1/span 2'
                  flex={1}
                  mt='s'
                  mb='m'
                >
                  <AlertBadge iconName='lightBulb' type='transparent' label={t('alert')} />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            display='flex'
            flex={genericResponsiveValue({
              breakPoints: { desktop: 4 },
              defaultValue: 10,
            })}
            style={{ gap: gapXS }}
            flexDirection='row'
            flexWrap='wrap'
            width='100%'
          >
            <Box width='100%'>
              <Form.FormFileInput
                label={t('uploadLogo.titleWhiteMode')}
                control={control}
                data-testid='upload-logo-light-mode'
                trigger={trigger}
                tooltip={t('uploadLogo.titleTooltipWhiteMode')}
                name='uploadLogoLight'
                supportedFileTypes={[FileTypes.PNG_IMAGES, FileTypes.JPEG_IMAGES, FileTypes.SVG]}
                value={logoLight}
              />
            </Box>
            <Box width='100%' mt='s'>
              <Form.FormFileInput
                label={t('uploadLogo.titleDarkMode')}
                control={control}
                data-testid='upload-logo-dark-mode'
                trigger={trigger}
                tooltip={t('uploadLogo.titleTooltipDarkMode')}
                name='uploadLogoDark'
                supportedFileTypes={[FileTypes.PNG_IMAGES, FileTypes.JPEG_IMAGES, FileTypes.SVG]}
                value={logoDark}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {loadingPostAspectGenerate && (
        <LoaderFullPage title={t('loading.title')} description={t('loading.description')} />
      )}
    </>
  );
};
